import React, { useState, useEffect } from 'react';
import './InscriptionPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { inscriptionService } from '../services/inscriptionService';
import filiereService from '../services/filiereService';
import useSEO from '../hooks/useSEO';

function InscriptionPage() {
  useSEO({
    title: "Inscriptions en Ligne 2025-2026 | Admissions CPEG, CPET, CPFPA, ISP",
    description: "Inscrivez votre enfant au Groupe Scolaire et Universitaire Les Palmiers à Togba (Abomey-Calavi). Formulaire d'admission rapide de la 6ème à la Terminale et formations techniques.",
    keywords: "inscriptions scolaires Bénin 2025-2026, admission collège Togba, inscription lycée Les Palmiers Calavi, CPEG CPET inscriptions",
    canonicalUrl: "https://gsulespalmiers.bj/inscription"
  });

  const [filieres, setFilieres] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    date_naissance: '',
    lieu_naissance: '',
    adresse: '',
    filiere_id: '',
    niveau_etude: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFilieres = async () => {
      try {
        const response = await filiereService.getAll();
        setFilieres(response.data);
      } catch (err) {
        console.error('Erreur lors du chargement des filières:', err);
      }
    };

    fetchFilieres();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await inscriptionService.submit(formData);
      setSuccess(true);
      // Réinitialiser le formulaire
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        date_naissance: '',
        lieu_naissance: '',
        adresse: '',
        filiere_id: '',
        niveau_etude: ''
      });

      // Scroll to top
      window.scrollTo(0, 0);
    } catch (err) {
      setError('Une erreur est survenue lors de l\'inscription. Veuillez réessayer.');
      console.error('Erreur lors de l\'inscription:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="inscription-page">
      <Header />
      
      <section className="inscription-hero">
        <div className="container">
          <h1>Formulaire d'Inscription</h1>
          <p>Rejoignez le Lycée Les Palmiers et commencez votre parcours vers l'excellence</p>
        </div>
      </section>

      <section className="inscription-form-section">
        <div className="container">
          {success && (
            <div className="alert alert-success">
              <h3>✓ Inscription enregistrée avec succès !</h3>
              <p>Votre demande d'inscription a été reçue. Nous vous contacterons dans les plus brefs délais pour la suite du processus.</p>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="inscription-form">
            <div className="form-section">
              <h2>Informations personnelles</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Téléphone *</label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date de naissance *</label>
                  <input
                    type="date"
                    name="date_naissance"
                    value={formData.date_naissance}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Lieu de naissance *</label>
                  <input
                    type="text"
                    name="lieu_naissance"
                    value={formData.lieu_naissance}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Adresse complète *</label>
                <textarea
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  rows="3"
                  required
                  disabled={loading}
                ></textarea>
              </div>
            </div>

            <div className="form-section">
              <h2>Informations académiques</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Filière souhaitée *</label>
                  <select
                    name="filiere_id"
                    value={formData.filiere_id}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Sélectionnez une filière</option>
                    {filieres.map((filiere) => (
                      <option key={filiere.id} value={filiere.id}>
                        {filiere.titre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Niveau d'étude actuel *</label>
                  <select
                    name="niveau_etude"
                    value={formData.niveau_etude}
                    onChange={handleChange}
                    required
                    disabled={loading}
                  >
                    <option value="">Sélectionnez votre niveau</option>
                    <option value="3eme">3ème</option>
                    <option value="seconde">Seconde</option>
                    <option value="premiere">Première</option>
                    <option value="terminale">Terminale</option>
                    <option value="bac">Titulaire du BAC</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
                {loading ? 'Envoi en cours...' : 'Soumettre ma candidature'}
              </button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default InscriptionPage;
